import React from "react";
import { Box, Grid, InputLabel } from "@mui/material";
import AvatarPanel from "./AvatarPanel.tsx";
import ExplainPrompt from "./ExplainPrompt.tsx";
import ThumbUpPrompt from "./ThumbUpPrompt.tsx";
import ThumbDownPrompt from "./ThumbDownPrompt.tsx";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import CustomReactMarkDown from "../../utils/markdown-wrapper.tsx";
import { useTypedSelector } from "../../hooks.tsx";
import { i18n } from "@lingui/core";

interface TablePromptProps {
  children?: React.ReactNode;
  content: {
    rows: Array<{ [key: string]: any }>;
    columns: GridColDef[];
    clarification?: string;
  };
  feedback?: string | null;
  exchange_id?: string;
}

// Mini-example: DataGrid expects data in a specific format:
{
  /* <DataGrid
  columns={[{ field: 'username' }, { field: 'age' }]}
  rows={[
    {
      id: 1,
      username: '@MUI',
      age: 20,
    },
  ]}
/> */
}

export const validateTable = (rows: any[], columns: any[]): boolean => {
  // Check if we have data
  if (!rows || !columns || rows.length === 0 || columns.length === 0) {
    return false;
  }

  // Validate column structure
  if (
    !columns.every(
      (col) => typeof col === "object" && col !== null && "field" in col,
    )
  ) {
    return false;
  }

  // Get all column field names
  const columnFields = columns.map((col) => col.field);

  // Check each row has all required columns
  return rows.every((row) => {
    // Check if row is an object
    if (typeof row !== "object" || row === null) {
      return false;
    }

    // Check if row has all required columns
    return columnFields.every((field) => field in row);
  });
};

type ColumnType = "string" | "number" | "boolean" | "date" | "dateTime";

export const getColumnType = (rows: any[], field: string): ColumnType => {
  const isValidDate = (d: Date) => !Number.isNaN(d.valueOf());
  const isMidnight = (d: Date) =>
    d.getHours() === 0 &&
    d.getMinutes() === 0 &&
    d.getSeconds() === 0 &&
    d.getMilliseconds() === 0;

  // yyyy-mm-dd (date-only)
  const isISODateOnly = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);

  // yyyy-mm-dd[ T]HH:mm[:ss[.sss]][Z|±HH:mm]
  const isISODateTimeString = (s: string) =>
    /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+\-]\d{2}:?\d{2})?$/.test(
      s,
    );

  for (const row of rows) {
    const v = row?.[field];

    // skip nullish/empty
    if (v == null) continue;
    if (typeof v === "string" && v.trim() === "") continue;

    // primitives
    if (typeof v === "boolean") return "boolean";
    if (typeof v === "number" && Number.isFinite(v)) return "number";

    // Date instances
    if (v instanceof Date && isValidDate(v)) {
      return isMidnight(v) ? "date" : "dateTime";
    }

    // Strings
    if (typeof v === "string") {
      const s = v.trim();

      if (isISODateTimeString(s)) {
        const d = new Date(s);
        if (isValidDate(d)) return "dateTime";
      }
      if (isISODateOnly(s)) {
        const d = new Date(s);
        if (isValidDate(d)) return "date";
      }

      return "string";
    }

    // Fallback
    return "string";
  }

  return "string";
};

const TablePrompt: React.FC<TablePromptProps> = ({
  feedback,
  exchange_id,
  content: { rows, columns, clarification },
}) => {
  const isLoggedIn = useTypedSelector((state) => state.authState.isLoggedIn);

  // Transform data to include unique IDs for DataGrid
  const rowsWithIds = rows.map((row, index) => ({
    id: index,
    ...row,
  }));

  const styledColumns: GridColDef[] = columns.map((col) => {
    const columnType = getColumnType(rowsWithIds, col.field); // e.g. "number" | "string" | ...

    const toDate = (v: unknown): Date | null => {
      if (v == null) return null;
      if (v instanceof Date && !Number.isNaN(v.valueOf())) return v;
      if (typeof v === "number" && Number.isFinite(v)) {
        const d = new Date(v);
        return Number.isNaN(d.valueOf()) ? null : d;
      }
      if (typeof v === "string" && v.trim() !== "") {
        const t = Date.parse(v);
        return Number.isNaN(t) ? null : new Date(t);
      }
      return null;
    };

    // --- Formatters ---
    const numberFmt = new Intl.NumberFormat(i18n?.locale, {
      style: "decimal",
      maximumFractionDigits: 2,
    });

    const dateFmt = new Intl.DateTimeFormat(i18n?.locale, {
      dateStyle: "medium",
    });

    const dateTimeFmt = new Intl.DateTimeFormat(i18n?.locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });

    // --- Formatter selection ---
    const valueFormatter: GridColDef["valueFormatter"] =
      columnType === "number"
        ? (value: any) => {
            if (value == null || !Number.isFinite(Number(value))) return "";
            return numberFmt.format(Number(value));
          }
        : columnType === "date"
          ? (value: any) => {
              const d = toDate(value);
              return d ? dateFmt.format(d) : "";
            }
          : columnType === "dateTime"
            ? (value: any) => {
                const d = toDate(value);
                return d ? dateTimeFmt.format(d) : "";
              }
            : columnType === "boolean"
              ? (value: any) => (value == null ? "" : String(Boolean(value)))
              : (value: any) => (value == null ? "" : String(value));

    return {
      ...col,
      headerClassName: "custom-column-header",
      flex: 1,
      type: columnType as GridColDef["type"],
      valueFormatter,
    };
  });

  return (
    <Box marginBottom={"4%"}>
      <Grid
        container
        direction="row"
        spacing={2}
        alignContent="flex-start"
        alignItems="flex-start"
      >
        <Grid
          size={{ xs: 1, lg: 1 }}
          sx={{
            marginTop: "1%",
          }}
        >
          <AvatarPanel />
        </Grid>
        <Grid size={{ xs: 11, lg: 11 }}>
          <InputLabel
            sx={{
              color: "black",
              whiteSpace: "normal",
            }}
          >
            <div>
              {validateTable(rowsWithIds, styledColumns) ? (
                <DataGrid
                  hideFooterSelectedRowCount
                  rows={rowsWithIds}
                  columns={styledColumns}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 10, page: 0 },
                    },
                  }}
                  pageSizeOptions={[10, 25, 50]}
                  showToolbar
                />
              ) : (
                <Box sx={{ p: 2, color: "error.main" }}>
                  Invalid table data. Please check that all required columns are
                  present.
                </Box>
              )}
            </div>
            <Box marginBottom={"1%"} marginTop={"2%"}>
              {clarification && (
                <CustomReactMarkDown
                  content={clarification}
                ></CustomReactMarkDown>
              )}
            </Box>
          </InputLabel>
        </Grid>
      </Grid>
      <Grid position={"relative"} sx={{ marginLeft: "8%", marginBottom: "5%" }}>
        <Grid container direction="row" position={"absolute"}>
          {feedback !== "bad" && (
            <Grid>
              <ThumbUpPrompt
                exchange_id={exchange_id}
                clicked={feedback === "good"}
              />
            </Grid>
          )}
          {feedback !== "good" && (
            <Grid>
              <ThumbDownPrompt
                exchange_id={exchange_id}
                clicked={feedback === "bad"}
              />
            </Grid>
          )}
          {isLoggedIn && (
            <Grid>
              <ExplainPrompt exchange_id={exchange_id} />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default TablePrompt;
